import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ExportButtons } from '@/components/ExportButtons';
import api from '@/lib/api';
import { PurchaseVatTab } from '@/components/accounting/PurchaseVatTab';
import { ShieldCheck, Lock } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = new Date().toISOString().slice(0, 10);
const monthStart = today.slice(0, 8) + '01';

const VAT_COLS = ['date', 'invoice', 'id', 'branch', 'gross', 'net_now', 'vat_now', 'net_correct', 'vat_correct', 'vat_over', 'zatca_status', 'journal', 'journal_vat', 'vat_account', 'revenue_account'];
const EXP_COLS = ['date', 'id', 'branch', 'category', 'amount', 'vat_amount', 'payment_mode', 'description'];
const GROUPS = [
  ['genuine_missing', 'Genuinely missing journal'],
  ['salary_payment_missing', 'Salary payments without journal'],
  ['mirror_of_missing_salary_payment', 'Expense copy of an unposted salary (same money)'],
  ['salary_mirror_accounted_by_payroll', 'Already accounted through payroll'],
  ['supplier_repayment_not_expense', 'Supplier repayments (not new expenses)'],
  ['possible_duplicates', 'Possible duplicates'],
];

const Table = ({ rows, cols, testid }) => (
  <div className="overflow-x-auto border rounded-md" data-testid={testid}>
    <table className="w-full text-xs">
      <thead className="bg-muted/50"><tr>{cols.map((c) => <th key={c} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{c.replace(/_/g, ' ')}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="p-3 text-muted-foreground">No records</td></tr>}
        {rows.map((r, i) => (
          <tr key={(r.id || '') + i} className="border-t">
            {cols.map((c) => <td key={c} className="px-2 py-1 whitespace-nowrap">{typeof r[c] === 'number' ? fmt(r[c]) : (r[c] === null || r[c] === undefined ? '—' : typeof r[c] === 'object' ? JSON.stringify(r[c]) : String(r[c]))}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Pager = ({ page, setPage, hasMore }) => (
  <div className="flex gap-2 items-center text-xs">
    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev-page">Prev</Button>
    <span data-testid="audit-page-number">Page {page}</span>
    <Button size="sm" variant="outline" disabled={!hasMore} onClick={() => setPage(page + 1)} data-testid="audit-next-page">Next</Button>
  </div>
);

const Stat = ({ label, value, testid }) => (
  <div className="rounded-md border p-3" data-testid={testid}><div className="text-xs text-muted-foreground">{label}</div><div className="text-lg font-semibold">{value}</div></div>
);

function VatFormulaTab({ params, page, setPage }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/data-integrity/vat-formula-audit', { params: { ...params, page } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [params, page]);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Affected sales" value={`${d.affected} of ${d.checked}`} testid="vat-audit-affected" />
        <Stat label="Recorded VAT" value={fmt(d.totals.vat_now)} testid="vat-audit-recorded" />
        <Stat label="Correct VAT" value={fmt(d.totals.vat_correct)} testid="vat-audit-correct" />
        <Stat label="VAT overstated" value={fmt(d.totals.vat_over)} testid="vat-audit-over" />
        <Stat label="ZATCA status" value={Object.entries(d.by_zatca_status || {}).map(([k, v]) => `${k}: ${v}`).join(', ') || '—'} testid="vat-audit-zatca" />
      </div>
      <div className="text-xs space-y-1" data-testid="vat-audit-proposal">
        <div className="font-medium">Proposed correction (review only, not posted):</div>
        {(d.dry_run_journal || []).map((j, i) => <div key={i}>Dr {j.debit_vat_payable} (VAT payable) / Cr {j.credit_revenue} (revenue): {fmt(j.amount)}</div>)}
        <div className="text-muted-foreground">{d.note}</div>
      </div>
      <div className="flex justify-between items-center"><Pager page={page} setPage={setPage} hasMore={d.rows.length >= 100} /><ExportButtons data={d.rows} columns={VAT_COLS} filename="vat_formula_audit" testid="vat-audit-export" /></div>
      <Table rows={d.rows} cols={VAT_COLS} testid="vat-audit-table" />
    </div>
  );
}

function MissingJournalTab({ params, page, setPage }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/data-integrity/unjournaled-expenses', { params: { ...params, page } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [params, page]);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const more = GROUPS.some(([k]) => (d[k] || []).length >= 100);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {GROUPS.map(([k, l]) => <Stat key={k} label={l} value={`${d.summary[k]?.count || 0} · ${fmt(d.summary[k]?.amount)}`} testid={`mj-summary-${k}`} />)}
      </div>
      <p className="text-xs text-muted-foreground">{d.note}</p>
      <Pager page={page} setPage={setPage} hasMore={more} />
      {GROUPS.map(([k, l]) => (
        <div key={k} className="space-y-2">
          <div className="flex justify-between items-center"><h3 className="text-sm font-medium">{l}</h3><ExportButtons data={d[k]} columns={[...EXP_COLS, 'possible_existing_journal', 'reason']} filename={`missing_journal_${k}`} testid={`mj-export-${k}`} /></div>
          <Table rows={d[k] || []} cols={k === 'possible_duplicates' ? ['date', 'id', 'branch', 'category', 'amount', 'reason'] : k === 'salary_payment_missing' ? ['date', 'id', 'employee', 'branch', 'amount', 'payment_mode', 'possible_existing_journal'] : [...EXP_COLS, 'supplier', 'possible_existing_journal']} testid={`mj-table-${k}`} />
        </div>
      ))}
    </div>
  );
}

function InputVatTab({ params }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/data-integrity/input-vat-check', { params }).then((r) => setD(r.data)).catch(() => setD(null)); }, [params]);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Expenses with VAT" value={`${d.expenses_with_vat.count} · ${fmt(d.expenses_with_vat.vat)}`} testid="ivat-with" />
        <Stat label="Expenses without VAT" value={`${d.expenses_without_vat.count} · ${fmt(d.expenses_without_vat.amount)}`} testid="ivat-without" />
        <Stat label="Bills VAT" value={`${d.bills.count} · ${fmt(d.bills.tax)}`} testid="ivat-bills" />
        <Stat label="GL input VAT accounts" value={d.gl_input_vat_accounts.map((a) => `${a.account}: ${fmt(a.debit - a.credit)}`).join(', ') || 'none'} testid="ivat-gl" />
      </div>
      <p className="text-xs text-muted-foreground">{d.note}</p>
      <Table rows={d.expenses_without_vat.by_category} cols={['category', 'kind', 'count', 'amount']} testid="ivat-by-category" />
    </div>
  );
}

export default function FinancialAuditsPage() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('all');
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [params, setParams] = useState({ start_date: monthStart, end_date: today });
  const [page, setPage] = useState(1);
  const [denied, setDenied] = useState('');

  useEffect(() => { api.get('/branches').then((r) => setBranches(r.data || [])).catch(() => {}); }, []);
  const run = useCallback(() => {
    const p = { start_date: start, end_date: end, ...(branchId !== 'all' ? { branch_id: branchId } : {}) };
    api.get('/data-integrity/input-vat-check', { params: p }).then(() => { setDenied(''); setPage(1); setParams(p); })
      .catch((e) => setDenied(e?.response?.data?.detail || 'Access denied'));
  }, [start, end, branchId]);
  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardLayout>
      <div className="space-y-5 p-1" data-testid="financial-audits-page">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" /><h1 className="text-2xl font-semibold">Financial Audits</h1>
          <Badge variant="outline" className="gap-1" data-testid="audit-readonly-badge"><Lock className="h-3 w-3" />Read only — nothing is posted</Badge></div>
        <Card><CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <div><div className="text-xs mb-1">From</div><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="audit-start-date" /></div>
          <div><div className="text-xs mb-1">To</div><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="audit-end-date" /></div>
          <div className="w-48"><div className="text-xs mb-1">Branch</div>
            <Select value={branchId} onValueChange={setBranchId}><SelectTrigger data-testid="audit-branch-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All permitted branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
          <Button onClick={run} data-testid="audit-run-btn">Run audit</Button>
        </CardContent></Card>
        {denied ? <p className="text-sm text-destructive" data-testid="audit-denied">{denied}</p> : (
          <Tabs defaultValue="vat" onValueChange={() => setPage(1)}>
            <TabsList><TabsTrigger value="vat" data-testid="tab-vat-formula">VAT Formula Audit</TabsTrigger><TabsTrigger value="mj" data-testid="tab-missing-journal">Missing-Journal Audit</TabsTrigger><TabsTrigger value="ivat" data-testid="tab-input-vat">Input VAT Check</TabsTrigger><TabsTrigger value="pvat" data-testid="tab-purchase-vat">Purchase VAT</TabsTrigger></TabsList>
            <TabsContent value="vat"><VatFormulaTab params={params} page={page} setPage={setPage} /></TabsContent>
            <TabsContent value="mj"><MissingJournalTab params={params} page={page} setPage={setPage} /></TabsContent>
            <TabsContent value="ivat"><InputVatTab params={params} /></TabsContent>
            <TabsContent value="pvat"><PurchaseVatTab params={params} page={page} setPage={setPage} /></TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
